import React, { useId, useState } from 'react';
import cn from 'classnames';

import { COUNTRIES, DEFAULT_COUNTRY_CODE, DEFAULT_STYLES } from '../../constants';
import { PhoneNumberFieldProps } from '../../types';

import { FieldErrorMessage } from '../FieldErrorMessage';

const StaticCountryPrefix = (props: {
  prefixId: string;
  label: string;
  isDisabled: boolean;
  phoneFieldCountryBtnClassName?: string;
  phoneFieldCountryTextClassName?: string;
}) => {
  const {
    prefixId,
    label,
    isDisabled,
    phoneFieldCountryBtnClassName,
    phoneFieldCountryTextClassName
  } = props;

  return (
    <span
      id={prefixId}
      className={cn(
        'flex items-center self-stretch rounded-l-lg text-sm -ml-4 pl-4 pr-0 pointer-events-none select-none shrink-0',
        isDisabled && 'opacity-50',
        phoneFieldCountryBtnClassName
      )}
    >
      <span
        style={{ color: '#475569', marginRight: '8px' }}
        className={cn(
          'text-slateTextColor600 font-medium',
          phoneFieldCountryTextClassName
        )}
      >
        {label}
      </span>
    </span>
  );
};

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = (props) => {
  const {
    name,
    label,
    phoneNumber = '',
    countryCode = DEFAULT_COUNTRY_CODE,
    onChangePhoneNumber,
    onBlur,
    onFocus,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    placeholder = 'Enter phone number',
    className,
    labelClassName,
    inputClassName,
    phoneFieldContainerClassName,
    phoneFieldCountryBtnClassName,
    phoneFieldCountryTextClassName,
    phoneFieldErrorTextClassName,
    phoneFieldHelperTextClassName,
    rightElement,
    rightButtonText,
    rightButtonAction,
    externalButtonText,
    externalButtonAction,
    externalButtonClassName,
    externalButtonElement
  } = props;

  const inputId = useId();
  const prefixId = `${inputId}-country-prefix`;
  const [isInputFocused, setIsInputFocused] = useState(false);

  const hasExternalButton =
    Boolean(externalButtonElement) || Boolean(externalButtonText);

  const selectedCountry =
    COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  const staticPrefixLabel = `${selectedCountry.code} (${selectedCountry.dialCode})`;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    onChangePhoneNumber?.(value);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(true);
    onFocus?.(e);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsInputFocused(false);
    onBlur?.(e);
  };

  const hasError = Boolean(error);

  const showError = hasError && !isInputFocused;

  const showHelperText = (!hasError || isInputFocused) && helperText;

  const describedByIds = [prefixId, hasError ? `${inputId}-error` : null]
    .filter(Boolean)
    .join(' ');

  const inputClassNames = cn(
    DEFAULT_STYLES.field__input,
    isDisabled
      ? DEFAULT_STYLES.field__inputDisabled
      : DEFAULT_STYLES.field__inputText,
    DEFAULT_STYLES.field__inputPlaceholder,
    rightElement || rightButtonText ? 'rounded-r-none' : 'rounded-r-lg',
    inputClassName
  );

  const renderTelInput = () => (
    <input
      id={inputId}
      name={name}
      type="tel"
      value={phoneNumber}
      placeholder={placeholder}
      onChange={handlePhoneNumberChange}
      onBlur={handleInputBlur}
      onFocus={handleInputFocus}
      disabled={isDisabled}
      aria-invalid={hasError}
      aria-describedby={describedByIds ? describedByIds : undefined}
      className={inputClassNames}
      style={isDisabled ? { WebkitTextFillColor: '#94a3b8' } : undefined}
    />
  );

  const renderInputRowContent = () => (
    <>
      <StaticCountryPrefix
        prefixId={prefixId}
        label={staticPrefixLabel}
        isDisabled={isDisabled}
        phoneFieldCountryBtnClassName={phoneFieldCountryBtnClassName}
        phoneFieldCountryTextClassName={phoneFieldCountryTextClassName}
      />
      {renderTelInput()}
      {rightElement && (
        <div
          className={cn(
            DEFAULT_STYLES.field__sideElement,
            'pr-2 rounded-r-lg'
          )}
        >
          {rightElement}
        </div>
      )}
      {rightButtonText && !rightElement && (
        <button
          type="button"
          onClick={rightButtonAction}
          disabled={isDisabled}
          className={cn(
            'flex-shrink-0 px-3 py-2 text-sm font-medium rounded-r-lg',
            'text-royalBlue hover:bg-zumthor transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
            isDisabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {rightButtonText}
        </button>
      )}
    </>
  );

  return (
    <div
      className={cn(
        DEFAULT_STYLES.field,
        className,
        'base__phone__number__field'
      )}
    >
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      <div className="flex flex-col">
        {hasExternalButton && (
          <div
            key={`external-button-wrapper-${hasExternalButton}`}
            className="hidden sm:flex sm:flex-row sm:items-stretch order-1"
          >
            <div
              className={cn(
                DEFAULT_STYLES.field__inputContainer,
                showError
                  ? 'border-errorRed'
                  : DEFAULT_STYLES.field__inputContainerDefault,
                isInputFocused &&
                !showError &&
                'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
                isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
                'sm:flex-1',
                phoneFieldContainerClassName
              )}
            >
              {renderInputRowContent()}
            </div>

            {externalButtonElement != null ? (
              <div
                key={`external-button-element-${externalButtonElement != null}`}
                className={cn(
                  'flex items-center flex-shrink-0',
                  externalButtonClassName
                )}
              >
                {externalButtonElement}
              </div>
            ) : externalButtonText != null ? (
              <div
                className={cn(
                  'flex items-center flex-shrink-0',
                  externalButtonClassName
                )}
              >
                <button
                  type="button"
                  onClick={externalButtonAction}
                  disabled={isDisabled}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg',
                    'bg-royalBlue text-white hover:bg-darkCerulean',
                    'transition-colors focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {externalButtonText}
                </button>
              </div>
            ) : null}
          </div>
        )}

        <div
          className={cn(
            DEFAULT_STYLES.field__inputContainer,
            showError
              ? 'border-errorRed ring-errorRed ring-opacity-50'
              : DEFAULT_STYLES.field__inputContainerDefault,
            isInputFocused &&
            !showError &&
            'border-lightIndigo600 ring-1 ring-lightIndigo600 input-container-focused',
            isDisabled && DEFAULT_STYLES.field__inputContainerDisabled,
            'order-1',
            hasExternalButton && 'sm:hidden',
            !hasExternalButton,
            phoneFieldContainerClassName
          )}
        >
          {renderInputRowContent()}
        </div>

        {(hasError || showHelperText) && (
          <p
            id={`${inputId}-error`}
            className={cn(
              DEFAULT_STYLES.field__message,
              'order-2',
              showError
                ? cn('text-errorTextColor', phoneFieldErrorTextClassName)
                : showHelperText
                  ? cn('text-helperTextColor', phoneFieldHelperTextClassName)
                  : cn(phoneFieldHelperTextClassName, 'invisible h-4')
            )}
            role={showError ? 'alert' : undefined}
          >
            {showError && error ? (
              <FieldErrorMessage message={error} />
            ) : showHelperText ? (
              helperText
            ) : (
              ''
            )}
          </p>
        )}
        {externalButtonElement != null ? (
          <div
            key={`external-button-mobile-${externalButtonElement != null}`}
            className={cn(
              'flex-col sm:hidden items-center w-full order-3',
              externalButtonClassName
            )}
          >
            {externalButtonElement}
          </div>
        ) : externalButtonText != null ? (
          <div
            className={cn(
              'flex-col sm:hidden items-center w-full order-3',
              externalButtonClassName
            )}
          >
            <button
              type="button"
              onClick={externalButtonAction}
              disabled={isDisabled}
              className={cn(
                'px-4 py-2 text-sm font-medium rounded-lg w-full',
                'bg-royalBlue text-white hover:bg-darkCerulean',
                'transition-colors focus:outline-none focus:ring-2 focus:ring-lightIndigo600',
                isDisabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {externalButtonText}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

PhoneNumberField.displayName = 'PhoneNumberField';
